import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { UPDATE_PLUS_PTO } from 'constants/authorization'
import { find, prop, propOr, compose, isEmpty, propEq, pluck } from 'ramda'

import { setSelectedIdWithDrawer } from 'actions/navigator'

import { useSelector, useMakeSelector } from 'hooks/redux'
import { getDomainFragments, getIsPtoEditBlocked } from 'selectors/powerTakeOff'
import { getSelectedId } from 'selectors/navigator'
import { getValidationErrors, makeGetHasErrors } from 'selectors/validation'

import strings from 'utils/strings'
import { fragmentBooleanText } from 'i18n/displayConstants/powerTakeOff'

import HasRights from 'components/HasRights'
import { Radio, Tooltip } from 'components/patterns'
import {
  PendingChangesIcon,
  ErrorWarningIcon,
} from 'components/patterns/Icon/IconsStyled'
import { stringCompareKey } from 'utils/sort'
import { isEmptyOrNil } from 'utils/functional'
import {
  StyledTable,
  StyledDiv,
  StyledParagraph,
  StyledPill,
} from './DomainTable.style'

export const columns = [
  {
    key: 'name',
    title: strings.settings,
    dataIndex: 'name',
    width: 465,
    render: (_, { name, pending }) => {
      if (!pending) return name

      return (
        <>
          {name} <StyledPill>{strings.pending}</StyledPill>
        </>
      )
    },
    sorter: stringCompareKey('name'),
    defaultSortOrder: 'ascend',
  },
  {
    key: 'value',
    title: strings.value,
    dataIndex: 'configuration',
    width: 90,
    render: (
      _,
      { id, configuration: { value, definedValues, translations, type } }
    ) => {
      if (!value && isEmpty(definedValues)) return strings.n_a

      const getTranslation = compose(
        fragmentBooleanText,
        prop('value'),
        find(propEq('value', value))
      )

      const translatedValue =
        type === 'boolean' ? getTranslation(translations) : value

      return <ValueComponent text={translatedValue} id={id} />
    },
  },
  {
    key: 'unit',
    title: strings.unit,
    dataIndex: 'configuration',
    width: 90,
    render: (_, { configuration }) => {
      const { units } = configuration
      return units
    },
  },
  {
    key: 'moduleNo',
    title: strings.module,
    dataIndex: 'moduleNo',
    width: 90,
  },
  {
    key: 'refNo',
    title: strings.refNo,
    dataIndex: 'refNo',
    width: 90,
    sorter: ({ refNo: refA }, { refNo: refB }) => {
      const [prefixA, domainA] = refA.split('_')
      const [prefixB, domainB] = refB.split('_')

      if (parseInt(prefixA) === parseInt(prefixB))
        return `${domainA}`.localeCompare(domainB)

      return parseInt(prefixA) - parseInt(prefixB)
    },
  },
  {
    key: 'description',
    title: strings.description,
    dataIndex: 'description',
    sorter: stringCompareKey('description'),
  },
]

const ValueComponent = ({ text, id }) => {
  const hasError = useMakeSelector(makeGetHasErrors, { id })

  return <StyledParagraph hasError={hasError}>{text}</StyledParagraph>
}

ValueComponent.defaultProps = {
  text: '',
}

ValueComponent.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
}

const DomainTable = ({ domain }) => {
  const dispatch = useDispatch()
  const selectedId = useSelector(getSelectedId)
  const validationErrors = useSelector(getValidationErrors)
  const fragments = useSelector(getDomainFragments, { id: domain })
  const isPtoEditBlocked = useSelector(getIsPtoEditBlocked)

  const groupIds = useMemo(
    () =>
      fragments.reduce((acc, current) => {
        const { groupId } = current

        if (!groupId || acc.includes(groupId)) return acc

        return acc.concat(groupId)
      }, []),
    [fragments]
  )

  const onCheck = useCallback(
    (id) => {
      dispatch(setSelectedIdWithDrawer(id))
    },
    [dispatch]
  )

  const getGroupIdRefNos = (groupId) =>
    pluck('refNo', fragments.filter(propEq('groupId', groupId)))

  const radioColumn = {
    key: 'radio',
    width: 1,
    render: (
      _,
      {
        refNo,
        isFragmentCompatible,
        groupId,
        pending,
        configuration: { definedValues },
      }
    ) => {
      const isChecked = refNo === selectedId
      const hasError = groupId
        ? getGroupIdRefNos(groupId).some((id) => validationErrors.has(id))
        : validationErrors.has(refNo)

      const renderIcon = () => {
        if (hasError && !isPtoEditBlocked)
          return (
            <HasRights rights={[UPDATE_PLUS_PTO]}>
              <ErrorWarningIcon />
            </HasRights>
          )
        if (pending && !isPtoEditBlocked)
          return (
            <HasRights rights={[UPDATE_PLUS_PTO]}>
              <PendingChangesIcon />
            </HasRights>
          )

        return null
      }

      const disabled =
        isEmptyOrNil(definedValues) || isEmptyOrNil(definedValues)
      const renderCustomRadio = isFragmentCompatible ? (
        <Radio
          disabled={disabled}
          checked={isChecked}
          onClick={() => onCheck(refNo)}
        />
      ) : (
        <Tooltip
          overlayClassName="autoWidth"
          title={strings.pto_setting_compatible_powertrain}
          placement="right"
        >
          <Radio
            disabled={disabled}
            checked={isChecked}
            onClick={() => onCheck(refNo)}
          />
        </Tooltip>
      )

      return (
        <StyledDiv>
          {renderCustomRadio}
          {renderIcon()}
        </StyledDiv>
      )
    },
  }

  const allColumns = [radioColumn, ...columns]

  const handleClickOnRow = (data) => {
    const { definedValues } = data.configuration
    const disabled = isEmptyOrNil(definedValues) || isEmptyOrNil(definedValues)
    if (!disabled) {
      onCheck(data.refNo)
    }
  }

  return (
    domain && (
      <StyledTable
        dataSource={fragments}
        columns={allColumns}
        rowKey="refNo"
        groupIds={groupIds}
        rowClassName={(data) => {
          const { definedValues } = data?.configuration
          const disabled =
            isEmptyOrNil(definedValues) || isEmptyOrNil(definedValues)
          return `${propOr('', 'groupId')} ${
            !disabled ? 'rowStyle' : 'notAllowed'
          }`
        }}
        onRow={(data) => ({
          onClick: () => handleClickOnRow(data),
        })}
      />
    )
  )
}

DomainTable.propTypes = {
  domain: PropTypes.string,
}

export default DomainTable
